import { createAsyncThunk } from '@reduxjs/toolkit'

import { showToast } from '../../../components/Toasts'
import i18n from '../../../i18n'
import { RootState } from '../../../store/reducer'
import {
  PlannedTrade,
  PlannedTradeMutationPayload,
  PlannedTradeOutcome,
  PlannedTradeResponse,
  TradingPlanReportRequest,
  TradingPlanReportsResponse,
  TradingPlanRequestParams,
} from '../types'

import { selectFetchParams } from './selectors'

export const SLICE_NAME = 'tradingPlan'

const buildFormData = ({
  values,
  riskPercent,
  riskRewardRatio,
  accountId,
}: PlannedTradeMutationPayload) => {
  const formData = new FormData()

  const parseNumber = (value: string) => (value === '' ? null : Number(value))
  const appendIfValue = (key: string, value?: string | number | boolean | null) => {
    if (value === null || value === undefined || value === '') return
    formData.append(key, String(value))
  }

  appendIfValue('plannedDate', values.plannedDate)
  appendIfValue('symbol', values.symbol)
  appendIfValue('direction', values.direction)
  appendIfValue('technicalConfluences', values.technicalConfluences)
  appendIfValue('fundamentalOutlook', values.fundamentalOutlook)
  appendIfValue('entryPrice', parseNumber(values.entryPrice))
  appendIfValue('stopLoss', parseNumber(values.stopLoss))
  appendIfValue('takeProfit', parseNumber(values.takeProfit))
  appendIfValue('positionSize', parseNumber(values.positionSize))
  appendIfValue('contractSize', parseNumber(values.contractSize))
  appendIfValue('accountId', accountId)
  appendIfValue('tradeTriggerDate', values.tradeTriggerDate)
  appendIfValue('tradeCloseDate', values.tradeCloseDate)
  appendIfValue('outcome', values.outcome)
  appendIfValue(
    'missedReason',
    values.outcome === PlannedTradeOutcome.missed ? values.missedReason : '',
  )
  appendIfValue(
    'missedWasSuccessful',
    values.outcome === PlannedTradeOutcome.missed ? values.missedWasSuccessful : null,
  )
  appendIfValue('pnl', parseNumber(values.pnl))
  appendIfValue('deviated', values.deviated)
  appendIfValue('notes', values.notes)
  appendIfValue('riskPercent', riskPercent !== null ? riskPercent : null)
  appendIfValue('riskRewardRatio', riskRewardRatio !== null ? riskRewardRatio : null)

  if (values.screenshotBefore instanceof File) {
    formData.append('screenshotBefore', values.screenshotBefore)
  }

  if (values.screenshotAfter instanceof File) {
    formData.append('screenshotAfter', values.screenshotAfter)
  }
  if (values.removeScreenshotBefore) {
    formData.append('removeScreenshotBefore', 'true')
  }

  if (values.removeScreenshotAfter) {
    formData.append('removeScreenshotAfter', 'true')
  }

  return formData
}

const withColumns = (
  params: TradingPlanRequestParams,
  state: RootState,
): TradingPlanRequestParams => ({
  ...params,
  columns: state.tradingPlan.pagination.columns,
})

export const fetchPlannedTrades = createAsyncThunk<
  PlannedTradeResponse,
  TradingPlanRequestParams,
  { state: RootState }
>(`${SLICE_NAME}/fetchPlannedTrades`, async (params, { getState }) => {
  try {
    const state = getState()
    const { TradingPlanService } = await import('../TradingPlanService')
    const response = await TradingPlanService.getPlannedTrades(withColumns(params, state))
    return response.data
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw Error(message)
  }
})

export const fetchTradingPlanReports = createAsyncThunk<
  TradingPlanReportsResponse,
  TradingPlanReportRequest
>(`${SLICE_NAME}/fetchTradingPlanReports`, async (payload) => {
  try {
    const { TradingPlanService } = await import('../TradingPlanService')
    const response = await TradingPlanService.getReports(payload)
    return response.data
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw Error(message)
  }
})

export const createPlannedTrade = createAsyncThunk<
  PlannedTrade,
  PlannedTradeMutationPayload,
  { state: RootState }
>(`${SLICE_NAME}/createPlannedTrade`, async (payload, { getState, dispatch }) => {
  try {
    const { TradingPlanService } = await import('../TradingPlanService')
    const response = await TradingPlanService.createPlannedTrade(buildFormData(payload))

    const params = selectFetchParams(getState())
    if (params) {
      await dispatch(fetchPlannedTrades(params))
    }

    showToast.success(
      i18n.t('tradingPlanPage.notifications.created', 'Planned trade created successfully'),
    )
    return response.data
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw Error(message)
  }
})

export const updatePlannedTrade = createAsyncThunk<
  PlannedTrade,
  PlannedTradeMutationPayload,
  { state: RootState }
>(`${SLICE_NAME}/updatePlannedTrade`, async (payload, { getState, dispatch }) => {
  if (!payload.values.id) {
    throw Error('Missing planned trade identifier')
  }

  try {
    const { TradingPlanService } = await import('../TradingPlanService')
    const response = await TradingPlanService.updatePlannedTrade(
      payload.values.id,
      buildFormData(payload),
    )

    const params = selectFetchParams(getState())
    if (params) {
      await dispatch(fetchPlannedTrades(params))
    }

    showToast.success(
      i18n.t('tradingPlanPage.notifications.updated', 'Planned trade updated successfully'),
    )
    return response.data
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw Error(message)
  }
})

export const deletePlannedTrade = createAsyncThunk<number, number, { state: RootState }>(
  `${SLICE_NAME}/deletePlannedTrade`,
  async (id, { getState, dispatch }) => {
    try {
      const { TradingPlanService } = await import('../TradingPlanService')
      await TradingPlanService.deletePlannedTrade(id)

      const params = selectFetchParams(getState())
      if (params) {
        await dispatch(fetchPlannedTrades(params))
      }

      showToast.success(
        i18n.t('tradingPlanPage.notifications.deleted', 'Planned trade deleted successfully'),
      )
      return id
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      throw Error(message)
    }
  },
)
